/**
 * Public journey runtime. Zero dependencies, enqueued in <head>, reads the
 * same inline JSON config the A/B runtime does.
 *
 * It answers three questions the A/B module cannot: who saw a link to a form
 * page and did not go there, how the people who did go there arrived and from
 * which button, and what happens inside the form itself.
 *
 * Three rules shape everything below.
 *
 *  - Every metric is counted ONCE PER VISITOR per destination, de-duplicated
 *    in localStorage. Without that, "saw the button minus arrived" is not a
 *    subtraction of comparable numbers.
 *  - Nothing runs without statistics consent, and nothing runs for internal
 *    traffic or for an editor previewing an experiment.
 *  - Destination matching is a client-side decision, because this site
 *    navigates through Swup and a server round trip per navigation would be
 *    both slow and, behind WP Engine's page cache, unreliable.
 */

import type { CtaRef, JourneyEvent, PageConfig, PublicDestination, TimingMetric } from './types.ts';
import { enqueue, flush, initBeacon } from './lib/beacon.ts';
import { hasConsent, onConsentGranted } from './lib/consent.ts';
import { getCookie } from './lib/cookies.ts';
import { matchTargeting } from './lib/url-match.ts';
import { readPreview } from './lib/preview.ts';
import { getDevice, getVisitorType, initVisitorType } from './lib/visitor.ts';
import * as cta from './journey/cta.ts';
import * as forms from './journey/forms.ts';
import * as trail from './journey/trail.ts';

const SESSION_FLAG = 'td_abt_js';

/**
 * Swup's head plugin swaps <head> tags on every navigation, and a <script>
 * that is removed and re-inserted RE-EXECUTES. So this bundle can be
 * instantiated several times in one page session, each copy registering its
 * own observers and listeners and holding its own in-memory snapshot of the
 * de-duplication state — which is how one visitor's submit got counted twice.
 *
 * The first instance stays fully functional across Swup navigations (it reads
 * the page id from the body class and matches destinations client-side), so
 * later instances have nothing to add and simply stand down. The flag lives on
 * window because that is the only thing the instances share.
 */
declare global {
	interface Window {
		__tdAbtJourneyBooted?: true;
	}
}

const config = readConfig();

if (config?.j?.length && config.jo && !window.__tdAbtJourneyBooted) {
	window.__tdAbtJourneyBooted = true;
	start(config);
}

function readConfig(): PageConfig | null {
	const el = document.getElementById('td-abt-config');
	if (!el?.textContent) return null;
	try {
		return JSON.parse(el.textContent) as PageConfig;
	} catch {
		return null;
	}
}

function start(cfg: PageConfig): void {
	// Preview mode and internal traffic silence tracking exactly as they do in
	// the A/B runtime: staff and editors must never move these numbers.
	const previewing = Object.keys(readPreview()).length > 0;
	const internalTraffic = getCookie('internal_traffic') === '1';
	if (previewing || internalTraffic) return;

	// The trail is statistics-purpose data, so nothing is written or sent
	// before consent. Boot is deferred rather than degraded.
	let booted = false;
	const bootOnce = (): void => {
		if (booted) return;
		booted = true;
		boot(cfg);
	};

	whenConsentApiSettled(() => {
		if (hasConsent('statistics')) {
			bootOnce();
			return;
		}
		onConsentGranted('statistics', bootOnce);
	});
}

/**
 * Wait until the WP Consent API has had its chance to announce itself, then
 * run the decision.
 *
 * This bundle is enqueued in <head>, so on a consent-managed site the API has
 * usually NOT defined window.wp_has_consent yet at this point. hasConsent()
 * reads a missing function as "no consent manager on this site, everything is
 * allowed" — correct for a site without a CMP, and badly wrong here: it would
 * start recording before the visitor's real answer is even knowable, which is
 * exactly the bug this guard exists to prevent.
 *
 * So: decide immediately when the API is already present, otherwise wait for
 * it to define itself, and fall back to the no-CMP reading only once the
 * document is parsed and nothing has appeared.
 *
 * The grace period alone was NOT enough: on a cached page WP Rocket's Delay
 * JS held the API's script back past it, and the decision ran with the API
 * still missing (td_abt_rv written ~1 s after DOMContentLoaded on production,
 * 2026-10-23). hasConsent() now fails closed whenever the page announces a
 * consent manager, so a late decision waits in onConsentGranted() instead.
 */
function whenConsentApiSettled(decide: () => void): void {
	if (typeof window.wp_has_consent === 'function') {
		decide();
		return;
	}

	let settled = false;
	const settle = (): void => {
		if (settled) return;
		settled = true;
		document.removeEventListener('wp_consent_type_defined', settle);
		document.removeEventListener('wp_listen_for_consent_change', settle);
		decide();
	};

	document.addEventListener('wp_consent_type_defined', settle);
	document.addEventListener('wp_listen_for_consent_change', settle);

	// Grace period after the document is parsed: by then every enqueued
	// script has run, so a still-missing function really does mean no CMP.
	const grace = (): void => {
		window.setTimeout(settle, 1000);
	};
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', grace);
	} else {
		grace();
	}
}

function boot(cfg: PageConfig): void {
	const destinations = cfg.j ?? [];
	const options = cfg.jo!;

	initBeacon(cfg.o.cu);
	initVisitorType();

	const base = (): Pick<JourneyEvent, 'k' | 'vt' | 'd'> => ({
		k: 'j',
		vt: getVisitorType(),
		d: getDevice(),
	});

	const send = (event: Omit<JourneyEvent, 'k' | 'vt' | 'd'>): void => {
		enqueue({ ...base(), ...event } as JourneyEvent);
	};

	// ----------------------------------------------------------------
	// Session
	// ----------------------------------------------------------------

	// One session row per browser session, recorded against no destination.
	// This is the funnel's denominator: the primary call to action lives in
	// the site header, so "saw the button" is very nearly "visited the site",
	// and sessions are the honest way to say it.
	try {
		if (window.sessionStorage.getItem(SESSION_FLAG) !== '1') {
			window.sessionStorage.setItem(SESSION_FLAG, '1');
			send({ m: 'session' });
		}
	} catch {
		/* storage blocked — the session simply is not counted */
	}

	// ----------------------------------------------------------------
	// Destination matching
	// ----------------------------------------------------------------

	const matchesPath = (destination: PublicDestination, path: string): boolean =>
		matchTargeting(destination.t, path, '').matched;

	const currentPath = (): string => window.location.pathname;

	const currentDestinations = (): PublicDestination[] =>
		destinations.filter((destination) => matchesPath(destination, currentPath()));

	const byGuid = (guid: string): PublicDestination | null => {
		if (guid) {
			const exact = destinations.find((destination) => destination.f.toLowerCase() === guid);
			if (exact) return exact;
		}
		// Fall back ONLY to a destination on this URL that declares no form of
		// its own. Pages here carry more than one form — /book-a-demo/ also
		// renders the footer newsletter — so falling back to "the first
		// destination on this URL" would count a newsletter signup as starting
		// the demo form. An unrecognised GUID on a page whose destination
		// names a specific form is simply not ours to measure.
		return currentDestinations().find((destination) => destination.f === '') ?? null;
	};

	// ----------------------------------------------------------------
	// Timings
	// ----------------------------------------------------------------

	/** Bucketed before sending: a duration per visitor would be a fingerprint. */
	const bucket = (ms: number): string => {
		const seconds = ms / 1000;
		if (seconds < 5) return '0-5s';
		if (seconds < 15) return '5-15s';
		if (seconds < 30) return '15-30s';
		if (seconds < 60) return '30-60s';
		if (seconds < 180) return '1-3m';
		if (seconds < 600) return '3-10m';
		return '10m+';
	};

	const timing = (metric: TimingMetric, ms: number): Array<{ m: TimingMetric; b: string }> =>
		ms > 0 && ms < 3600000 ? [{ m: metric, b: bucket(ms) }] : [];

	// ----------------------------------------------------------------
	// Buttons
	// ----------------------------------------------------------------

	const onVisible = (hit: cta.CtaHit): void => {
		const destKey = hit.destination.k;
		// Destination-level impression: the funnel denominator for this page.
		if (trail.claim('r', destKey)) {
			send({ m: 'cta_reach', x: destKey });
		}
		// Per-button impression: the breakdown that says WHICH button works.
		if (trail.claimCta('rc', destKey, hit.cta.k)) {
			send({ m: 'cta_reach', x: destKey, c: hit.cta });
		}
	};

	const rescan = (): void => {
		const onDestination = currentDestinations();
		cta.scan(
			document,
			destinations,
			matchesPath,
			// Skip a destination the visitor is already on. The header button
			// is still on the page it leads to, and counting that as an
			// impression would pad the denominator with people who arrived.
			(destination) => onDestination.includes(destination),
			onVisible,
			options.p,
		);
	};

	document.addEventListener(
		'click',
		(event) => {
			for (const hit of cta.hitsForEvent(event.target)) {
				const destKey = hit.destination.k;
				// Remembered even when the click is not the first, so the
				// arrival can always name the button that produced it.
				trail.rememberCta(destKey, hit.cta);
				if (trail.claimCta('cc', destKey, hit.cta.k)) {
					send({ m: 'cta_click', x: destKey });
					send({ m: 'cta_click', x: destKey, c: hit.cta });
				}
			}
		},
		true,
	);

	// ----------------------------------------------------------------
	// Arrival
	// ----------------------------------------------------------------

	const evaluateArrival = (): void => {
		trail.pushStep(readPostId(options.p), options.ms);

		for (const destination of currentDestinations()) {
			if (!trail.claim('a', destination.k)) continue;
			const entry = trail.entryCta(destination.k);
			const route = trail.steps();
			trail.markArrival(destination.k);
			trail.rememberArrivalRoute(destination.k, route, entry);
			// The split is load-bearing: without it, a visitor who arrives
			// from an email would push "did not arrive" below zero.
			if (entry) {
				send({ m: 'arrived', x: destination.k, t: route, c: entry });
			} else {
				send({ m: 'arrived_direct', x: destination.k, t: route });
			}
		}
	};

	/**
	 * The current page id. On a full load PHP printed it; after a Swup
	 * navigation PHP is not involved at all, but SwupBodyClassPlugin swaps the
	 * body classes, so `postid-N` / `page-id-N` is the id with no request.
	 */
	const readPostId = (fallback: number): number => {
		const match = /(?:^|\s)(?:postid|page-id)-(\d+)(?:\s|$)/.exec(document.body?.className ?? '');
		if (match?.[1]) return Number(match[1]);
		return fallback;
	};

	// ----------------------------------------------------------------
	// Forms
	// ----------------------------------------------------------------

	// Declared before init() rather than after: the callbacks below close over
	// these, and reading them from a hoisted-but-uninitialised const is a trap
	// that only shows up if a callback ever fires synchronously.
	const viewedAt = new Map<string, number>();
	const startedAt = new Map<string, number>();

	forms.init(
		{
			onView: (guid) => {
				const destination = byGuid(guid);
				if (!destination) return;
				viewedAt.set(destination.k, Date.now());
				if (trail.claim('fv', destination.k)) {
					send({ m: 'form_view', x: destination.k, fg: guid });
				}
			},
			onStart: (guid) => {
				const destination = byGuid(guid);
				if (!destination) return;
				startedAt.set(destination.k, Date.now());
				if (!trail.claim('fs', destination.k)) return;
				const viewed = viewedAt.get(destination.k);
				send({
					m: 'form_start',
					x: destination.k,
					fg: guid,
					tm: viewed ? timing('view_to_start', Date.now() - viewed) : [],
				});
			},
			onField: (guid, _container, event) => {
				const destination = byGuid(guid);
				if (!destination) return;
				// One row per visitor per field per metric. Without this the
				// table counts interactions, and "reached" climbs every time
				// somebody tabs back through the form.
				if (!trail.claimField(destination.k, guid, event.name, event.metric)) return;
				if (event.metric === 'error') {
					send({ m: 'form_error', x: destination.k, fg: guid, fl: [{ n: event.name, m: 'error' }] });
					return;
				}
				// Field rows travel on their own metric. Putting them on a real
				// funnel step would increment that step once per keystroke-ish
				// interaction and wreck the funnel.
				send({
					m: 'field',
					x: destination.k,
					fg: guid,
					fl: [{ n: event.name, m: event.metric, ...(event.ms ? { ms: event.ms } : {}) }],
				});
			},
			onSubmit: (guid) => {
				const destination = byGuid(guid);
				if (!destination) return;
				if (!trail.claim('fd', destination.k)) return;
				const started = startedAt.get(destination.k);
				const arrived = trail.arrivalAt(destination.k);
				// The arrival's own route and button, so the route row learns it ended in a
				// submission. Absent when the arrival predates this (nothing is credited).
				const route = trail.arrivalRoute(destination.k);
				send({
					m: 'form_submit',
					x: destination.k,
					fg: guid,
					...(route ? { t: route.t, ...(route.c ? { c: route.c } : {}) } : {}),
					tm: [
						...(started ? timing('start_to_submit', Date.now() - started) : []),
						...(arrived ? timing('arrive_to_submit', Date.now() - arrived) : []),
					],
				});
				// A submit is often followed by a redirect or a thank-you swap;
				// do not wait for the debounce to expire.
				flush();
			},
		},
		options.tf,
	);

	// ----------------------------------------------------------------
	// Boot + Swup wiring
	// ----------------------------------------------------------------

	const evaluate = (): void => {
		evaluateArrival();
		rescan();
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', evaluate);
	} else {
		evaluate();
	}

	// Swup replaces #main only, so this both re-evaluates the URL (a visitor
	// can reach the destination with no page load) and re-scans page content
	// for buttons. Header and footer links keep their single registration.
	document.addEventListener('swup:content:replace', () => {
		window.requestAnimationFrame(() => evaluate());
	});

	// bfcache restores land on a page that was never re-parsed.
	window.addEventListener('pageshow', (event) => {
		if (event.persisted) evaluate();
	});
}

export {};
